import { useEffect, useRef, useState } from 'react';
import { WorkflowTab } from '../../../Api/Orders/ordersApi';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { ScheduleSettingPayload, useScheduleSettings } from '../../../store/ScheduleSettings';
import { getUser } from '../../../store/User';
import { WORKFLOW_TAB_ORDER } from '../../Pages/Orders/workflowTabs';
import { useScheduleForm, UseScheduleFormReturn } from '../CustomTableViews/useScheduleForm';
import { SchedulePreview, useSchedulePreview } from '../CustomTableViews/useSchedulePreview';
import {
  includedTabsOf,
  newOrdersReportKey,
  OrdersReport,
  OrdersReportKey,
  OrdersReportSaveOutcome,
  saveOrdersReport
} from './ordersReport';

const SAVE_FAILED = 'The report could not be saved. Please try again.';
const TABS_NOT_SAVED = "The report's schedule was saved, but its tabs were not. Select Save again to store them.";

export interface UseOrdersReportFormReturn {
  scheduleForm: UseScheduleFormReturn;
  reportKey: OrdersReportKey;
  reportName: string;
  setReportName: (name: string) => void;
  includedTabs: WorkflowTab[];
  setIncludedTabs: (tabs: WorkflowTab[]) => void;
  preview: SchedulePreview;
  saving: boolean;
  canSave: boolean;
  save: () => Promise<void>;
}

/** The Schedule Report dialog's report: its name, tabs and schedule, seeded each time it opens, and its save. */
export const useOrdersReportForm = (
  open: boolean,
  existingSchedule: OrdersReport | null,
  onSaved: () => void
): UseOrdersReportFormReturn => {
  const { saveSchedule } = useScheduleSettings();
  const { setSnack } = useSnackbar();
  const scheduleForm = useScheduleForm(existingSchedule, getUser().email);
  const [reportKey, setReportKey] = useState<OrdersReportKey>(newOrdersReportKey);
  const [reportName, setReportName] = useState('');
  const [includedTabs, setIncludedTabs] = useState<WorkflowTab[]>(WORKFLOW_TAB_ORDER);
  const [saving, setSaving] = useState(false);
  // A new report's schedule the API stored but did not keep as sent, so that the next Save updates it under its key.
  const storedId = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Closing leaves the fields as they were, for the dialog to fade out on them rather than on a blank report.
    if (!open) return;
    storedId.current = undefined;
    const key = existingSchedule?.view_key ?? newOrdersReportKey();
    setReportKey(key);
    setReportName(existingSchedule?.name ?? '');
    setIncludedTabs(existingSchedule ? includedTabsOf(key) : WORKFLOW_TAB_ORDER);
    scheduleForm.reseed();
    // By id, not the object: a list read again while the dialog is open hands over a new copy of the same report.
  }, [open, existingSchedule?.id]);

  const preview = useSchedulePreview(open ? scheduleForm.rebuiltRule : null, scheduleForm.endDateValue);
  const trimmedName = reportName.trim();
  const canSave =
    !!trimmedName && includedTabs.length > 0 && scheduleForm.datesValid && scheduleForm.notAdded.length === 0;

  const buildPayload = (): ScheduleSettingPayload => ({
    name: trimmedName,
    ...scheduleForm.scheduleFields(),
    include_executive_summary: false,
    view_key: reportKey,
    views: [{ view_key: reportKey, tab_name: trimmedName }]
  });

  const reportFailure = (message = SAVE_FAILED) => setSnack({ message, type: 'error', open: true });

  const save = async () => {
    setSaving(true);
    let outcome: OrdersReportSaveOutcome | null = null;
    try {
      outcome = await saveOrdersReport({
        key: reportKey,
        tabs: includedTabs,
        editing: !!(existingSchedule || storedId.current),
        saveSchedule: (stored) =>
          saveSchedule(buildPayload(), storedId.current ?? existingSchedule?.id, {
            onStored: (schedule) => {
              storedId.current = schedule.id;
              stored();
            }
          })
      });
      if (outcome === 'saved') onSaved();
      if (outcome === 'tabs_not_stored') reportFailure();
      if (outcome === 'schedule_saved_without_tabs') reportFailure(TABS_NOT_SAVED);
    } catch {
      reportFailure();
    } finally {
      // A schedule whose answer was lost may exist under this key, and storing its tabs again would start it sending.
      if (!existingSchedule && !storedId.current && outcome !== 'saved') setReportKey(newOrdersReportKey());
      setSaving(false);
    }
  };

  return {
    scheduleForm,
    reportKey,
    reportName,
    setReportName,
    includedTabs,
    setIncludedTabs,
    preview,
    saving,
    canSave,
    save
  };
};
